import React, { useState, useEffect } from 'react';
import styles from './Surveys.module.scss';
import Table from '../../../components/table/Table';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Filter from '../../../components/filter/Filter';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const STATUS_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Expired', value: 'EXPIRED' },
];

const Surveys = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [data, setData] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1, total: 0, start: 1, end: 1, hasPrev: false, hasNext: false
  });

  const fetchSurveys = React.useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = { page, limit: 10 };
      if (statusFilter) params.status = statusFilter;
      const response = await axiosClient.get('/surveys/requests', { params });
      const responseData = response.data;
      setData(responseData.requests || []);
      const totalPages = Math.ceil(responseData.total / responseData.limit);
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < totalPages
      });
    } catch (error) {
      showToast(error.message || 'Failed to fetch surveys', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast, statusFilter]);

  useEffect(() => {
    fetchSurveys(1);
  }, [fetchSurveys]);

  const columns = [
    {
      header: 'Survey',
      key: 'survey',
      render: (row) =>
        row.status === 'ACTIVE' ? (
          <span
            className={styles.titleLink}
            onClick={() => navigate(`/surveys/${row.id}`)}
          >
            {row.survey?.title || '—'}
          </span>
        ) : (
          row.survey?.title || '—'
        ),
    },
    {
      header: 'Type',
      key: 'type',
      render: (row) => row.type === 'ClinicalSite' ? 'Clinical Site' : row.type,
    },
    {
      header: 'Related To',
      key: 'relatedTo',
      render: (row) =>
        row.type === 'Instructor'
          ? `${row.instructor?.firstName || ''} ${row.instructor?.lastName || ''}`.trim() || '—'
          : row.clinicalSite?.name || '—',
    },
    {
      header: 'Status',
      key: 'status',
      render: (row) => {
        const statusClass =
          row.status === 'SUBMITTED' ? styles.statusSubmitted :
          row.status === 'EXPIRED' ? styles.statusExpired :
          styles.statusActive;
        return (
          <div className={`${styles.statusBadge} ${statusClass}`}>
            <span className={styles.statusDot} />
            {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
          </div>
        );
      },
    },
    {
      header: 'Date',
      key: 'createdAt',
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Surveys" />
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={STATUS_FILTERS}
              selected={statusFilter}
              onChange={setStatusFilter}
            />
          </div>
        </div>
        <Table
          columns={columns}
          data={data}
          pagination={pagination}
          onPageChange={(page) => fetchSurveys(page)}
          emptyMessage="No surveys available."
        />
      </div>
    </>
  );
};

export default Surveys;
